import { resolveReferenceImageDisplayUrl } from '../../api/referenceUpload'
import type { ComicCharacterCard } from '../../lib/comicCharacters'
import { chromeSelectableMediaCardClass } from '../ChromeSurface'
import { ChromeTooltip } from '../ChromeTooltip'
import { IconPlusSmall } from '../nav-icons'
import { PortraitFocusFrame } from '../PortraitFocusFrame'
import { comicPanelRoundSm } from './comicPanelTokens'

const CARD_W = 'w-[7.25rem] sm:w-[8rem]'

type PreviewProps = {
  angular: boolean
  card: ComicCharacterCard
  selected: boolean
  onClick: () => void
  /** 分镜多选：显示勾选角标 */
  selectionMode?: 'single' | 'multi'
}

/** 角色卡横向条预览块（封面比例 + 名称，与书架/候选条同一套可选媒体表面） */
export function ComicCharacterCardPreviewTile({
  angular,
  card,
  selected,
  onClick,
  selectionMode = 'single',
}: PreviewProps) {
  const name = card.name.trim() || '未命名'
  const hasRef = Boolean(card.referenceImageUrl?.trim())
  const appearance = card.appearance.trim()

  const tooltipContent = appearance ? `${name}：${appearance}` : name

  return (
    <ChromeTooltip content={tooltipContent} angular={angular} side="top" align="start">
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={[
          CARD_W,
          'group flex shrink-0 flex-col overflow-hidden text-left outline-none transition',
          'focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--panel)]',
          chromeSelectableMediaCardClass(angular, selected),
          comicPanelRoundSm(angular),
        ].join(' ')}
      >
      <div className="relative">
        {hasRef ? (
          <PortraitFocusFrame
            src={resolveReferenceImageDisplayUrl(card.referenceImageUrl!)}
            alt=""
            compact
            frameClassName="relative aspect-[3/4] w-full overflow-hidden bg-surface-muted/50"
          />
        ) : (
          <div className="relative aspect-[3/4] w-full bg-surface-muted/50">
            <div
              className={[
                'absolute inset-0 flex items-center justify-center px-2 text-center text-[11px] font-semibold leading-snug text-ink-soft',
                'bg-[linear-gradient(165deg,color-mix(in_srgb,var(--accent)_10%,var(--surface-muted)),var(--surface-muted))]',
              ].join(' ')}
              aria-hidden
            >
              {appearance ? (
                <span className="line-clamp-4">{appearance}</span>
              ) : (
                '无参考图'
              )}
            </div>
          </div>
        )}
        {selectionMode === 'multi' && selected ? (
          <span className="pointer-events-none absolute left-1 top-1 z-[2] rounded-[var(--radius-sm)] bg-accent px-1.5 py-px text-[9px] font-bold text-white">
            出场
          </span>
        ) : null}
      </div>
      <div className="border-t border-line bg-[color-mix(in_srgb,var(--panel)_88%,transparent)] px-2 py-1.5">
        <p className="truncate text-xs font-semibold text-ink">{name}</p>
      </div>
      </button>
    </ChromeTooltip>
  )
}

type AddProps = {
  angular: boolean
  label?: string
  onClick: () => void
}

export function ComicCharacterCardAddTile({
  angular,
  label = '添加角色',
  onClick,
}: AddProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        CARD_W,
        'flex shrink-0 flex-col items-center justify-center gap-1.5 border border-dashed border-line bg-surface-muted/20 px-2 py-4 text-ink-soft transition',
        'hover:border-accent hover:bg-surface-muted/40 hover:text-ink outline-none focus-visible:ring-2 focus-visible:ring-accent',
        comicPanelRoundSm(angular),
      ].join(' ')}
      aria-label={label}
    >
      <span
        className={[
          'flex size-9 items-center justify-center border border-line bg-[var(--menu-solid)] text-ink',
          angular ? 'rounded-none' : 'rounded-full',
        ].join(' ')}
      >
        <IconPlusSmall className="h-4 w-4" aria-hidden />
      </span>
      <span className="text-center text-[11px] font-semibold leading-tight">{label}</span>
    </button>
  )
}
